import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import { ChoiceCard, Field, SelectField, StepAlert, ToggleRow, SecondaryButton, useInstallTheme } from "../ui";
import { contactKey, newClientRef } from "../installState";
import ContactAccessTable from "../../claim/ContactAccessTable";
import ThresholdFields from "../../claim/ThresholdFields";
import AlertSettings from "../../claim/AlertSettings";

export function ContactsStep({ state, dispatch, existingContacts, accessRows, dropdowns, t, darkMode }) {
  const contacts = state.contacts;
  const setContacts = (rows) => dispatch({ type: "SET_CONTACTS", contacts: rows });
  const update = (index, patch) =>
    setContacts(contacts.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  const add = () =>
    setContacts([
      ...contacts,
      { client_ref: newClientRef(), first_name: "", last_name: "", contact_type_id: "", email: "", phone: "", invite: false },
    ]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {(existingContacts || []).length > 0 && (
        <Box p={2} borderRadius="16px" sx={{ background: t.softSurface, border: `1px solid ${t.borderSubtle}` }}>
          <Typography variant="caption" sx={{ color: t.textMuted }}>
            Contacts already on this customer are kept. You can add more below.
          </Typography>
          {existingContacts.map((c, i) => {
            const a = c.attributes || c;
            return (
              <Typography key={a.contact_id ?? i} fontWeight={600}>
                {[a.contact_name || [a.first_name, a.last_name].filter(Boolean).join(" "), a.contact_type, ...(a.addresses || []).map((x) => x.value)]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            );
          })}
        </Box>
      )}

      {contacts.map((contact, index) => {
        const key = contactKey(contact, index);
        const billing = state.billingContactKey === key;
        return (
          <Box
            key={key}
            p={2}
            borderRadius="16px"
            sx={{ background: t.softSurfaceStrong, border: `1px solid ${t.borderSubtle}` }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography fontWeight={700}>{`Contact ${index + 1}`}</Typography>
              <IconButton
                aria-label={`Remove contact ${index + 1}`}
                size="small"
                onClick={() => {
                  setContacts(contacts.filter((_, i) => i !== index));
                  if (billing) dispatch({ type: "SET_BILLING_CONTACT", key });
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box display="flex" flexDirection="column" gap={2}>
              <Field label="First name" value={contact.first_name} onChange={(e) => update(index, { first_name: e.target.value })} t={t} darkMode={darkMode} />
              <Field label="Last name" value={contact.last_name} onChange={(e) => update(index, { last_name: e.target.value })} t={t} darkMode={darkMode} />
              <SelectField
                label="Role"
                value={contact.contact_type_id}
                onChange={(e) => update(index, { contact_type_id: e.target.value })}
                t={t}
                darkMode={darkMode}
              >
                {(dropdowns.contactTypes || []).map((row) => (
                  <MenuItem key={row.contact_type_id} value={String(row.contact_type_id)}>
                    {row.type_name || row.name}
                  </MenuItem>
                ))}
              </SelectField>
              <Field
                label="Email"
                type="email"
                value={contact.email}
                onChange={(e) => update(index, { email: e.target.value })}
                t={t}
                darkMode={darkMode}
              />
              <Field
                label="Mobile phone"
                type="tel"
                inputMode="tel"
                value={contact.phone}
                onChange={(e) => update(index, { phone: e.target.value })}
                t={t}
                darkMode={darkMode}
              />
              <ToggleRow
                label="Invite to Rivio account"
                checked={contact.invite}
                onChange={(checked) => update(index, { invite: checked })}
                description="Sends an account invitation where supported."
              />
              <ToggleRow
                label="Use this person for future billing"
                checked={billing}
                onChange={() => dispatch({ type: "SET_BILLING_CONTACT", key })}
                description="This person will receive future billing communication after any included service period. This does not authorize a payment today."
              />
            </Box>
          </Box>
        );
      })}

      <SecondaryButton onClick={add} t={t}>
        Add a contact
      </SecondaryButton>

      <Box p={2} borderRadius="16px" sx={{ background: t.softSurface, border: `1px solid ${t.borderSubtle}` }}>
        <Typography fontWeight={700} mb={1}>
          Contact device access
        </Typography>
        <ContactAccessTable
          rows={accessRows}
          onChange={(key, patch) => dispatch({ type: "SET_CONTACT_ACCESS", key, patch })}
        />
      </Box>
    </Box>
  );
}

export function DeviceDetailsStep({ state, overview, dispatch, t, darkMode }) {
  const deviceName = state.device.device_name || overview?.device?.name || "";
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Field
        label="Device name"
        value={state.device.device_name}
        onChange={(e) => dispatch({ type: "SET_DEVICE", device: { device_name: e.target.value } })}
        t={t}
        darkMode={darkMode}
        helperText={deviceName !== state.device.device_name ? `Suggested: ${deviceName}` : undefined}
        required
      />
      <Field
        label="Asset/map identifier"
        value={state.device.map_identifier}
        onChange={(e) => dispatch({ type: "SET_DEVICE", device: { map_identifier: e.target.value } })}
        t={t}
        darkMode={darkMode}
        required
      />
      <Field
        label="Installation date"
        type="date"
        value={state.device.installation_date}
        onChange={(e) => dispatch({ type: "SET_DEVICE", device: { installation_date: e.target.value } })}
        t={t}
        darkMode={darkMode}
        helperText="Defaults to today."
        required
      />
      {overview?.device?.serial ? (
        <Typography variant="caption" sx={{ color: t.textMuted }}>
          Serial: {overview.device.serial}
        </Typography>
      ) : null}
    </Box>
  );
}

export function ThresholdsStep({ state, dispatch, t }) {
  return (
    <ThresholdFields
      rows={state.thresholds}
      surfaceSx={{ background: t.softSurface, border: `1px solid ${t.borderSubtle}` }}
      emptyMessage="No alert levels are configured for this model."
      onChange={(index, patch) =>
        dispatch({
          type: "SET_THRESHOLDS",
          thresholds: state.thresholds.map((r, i) => (i === index ? { ...r, ...patch } : r)),
        })
      }
    />
  );
}

export function NotificationsStep({ state, dispatch, destinations, t }) {
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <StepAlert severity="info">
        Alert rules are saved after the monitor is assigned, so messages go to this customer&apos;s contacts. Each
        rule&apos;s saved result is shown on the completion page.
      </StepAlert>
      <Box p={2} borderRadius="16px" sx={{ background: t.softSurface, border: `1px solid ${t.borderSubtle}` }}>
        <AlertSettings
          alerts={state.alerts}
          destinations={destinations}
          thresholds={state.thresholds}
          onChange={(alerts) => dispatch({ type: "SET_ALERTS", alerts })}
        />
      </Box>
    </Box>
  );
}

export function ProviderSharingStep({ state, dispatch, t }) {
  const options = state.providerOptions || [];
  if (!options.length) {
    return (
      <StepAlert severity="info">
        No approved partners can be shared at this time.
      </StepAlert>
    );
  }
  return (
    <Box>
      <Typography variant="body2" sx={{ color: t.textMuted, mb: 2 }}>
        Choose approved partners that should have access to this asset. Sharing applies to the installed tank / site
        the device is assigned to, and is shown as Shared only after it is saved.
      </Typography>
      {options.map((provider) => {
        const id = provider.provider_id ?? provider.id;
        const checked = state.providerSelection.includes(id);
        const relation = provider.relationship || (provider.is_direct ? "Direct" : "Approved");
        return (
          <ChoiceCard
            key={id}
            title={provider.name || provider.provider_name || `Provider ${id}`}
            description={relation}
            selected={checked}
            onClick={() => dispatch({ type: "TOGGLE_PROVIDER", providerId: id })}
            t={t}
          />
        );
      })}
    </Box>
  );
}
